import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../context/CartContext";
import { toast } from "react-toastify";
import Navbar from "../components/Navbar";

const CartPage = () => {
  const { cart, updateQuantity, removeItem, clearCart } =
    useContext(CartContext);
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN").format(price);
  };

  const calculateTotal = () => {
    return cart.items.reduce((total, item) => {
      return total + (item.price || 0) * (item.qty || 0);
    }, 0);
  };

  const getTotalItems = () => {
    return cart.items
      .filter((i) => !i.isGift)
      .reduce((total, item) => total + (item.qty || 0), 0);
  };

  const handleRemoveItem = (item) => {
    removeItem(item.productId, item.selectedColor, item.selectedSize);
    toast.success("Đã xóa sản phẩm khỏi giỏ hàng!");
  };

  const handleUpdateQuantity = (item, newQty) => {
    if (newQty < 1) {
      handleRemoveItem(item);
      return;
    }

    updateQuantity(
      item.productId,
      item.selectedColor,
      item.selectedSize,
      newQty,
    );
    toast.success("Đã cập nhật số lượng!");
  };

  const handleClearCart = () => {
    if (window.confirm("Bạn có chắc muốn xóa toàn bộ giỏ hàng?")) {
      clearCart();
      toast.success("Đã xóa toàn bộ giỏ hàng!");
    }
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div 
        className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col transition-colors duration-300"
        style={{ 
          zoom: "0.8",
          MozTransform: "scale(0.8)",
          MozTransformOrigin: "top center"
        }}
      >
        <Navbar />
        <div className="flex-grow flex items-center justify-center py-20 md:py-36 px-4">
          <div className="text-center">
            <div className="w-32 h-32 bg-gray-100 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="material-symbols-outlined text-[64px] text-gray-400">
                shopping_cart
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
              Giỏ hàng trống
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-xs mx-auto">
              Bạn chưa có sản phẩm nào trong giỏ hàng của mình.
            </p>
            <Link
              to="/products"
              className="inline-block px-8 py-3.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 dark:shadow-none active:scale-95 uppercase tracking-wider"
            >
              Tiếp tục mua sắm
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gray-50 dark:bg-slate-950 flex flex-col transition-colors duration-300"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <Navbar />

      <div className="flex-grow max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8 mt-24 pt-2 sm:pt-14 pb-24 sm:pb-8">
        <div className="flex items-center justify-between mb-4 sm:mb-8 px-2 sm:px-0">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Giỏ hàng của bạn
            </h1>
            <p className="text-xs sm:text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1">
              {getTotalItems()} sản phẩm
            </p>
          </div>
          <button
            onClick={handleClearCart}
            className="px-3 py-1.5 text-xs sm:text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors font-medium"
          >
            Xóa tất cả
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {cart.items.map((item, index) => (
              <div
                key={`${item.productId}-${item.selectedColor}-${item.selectedSize}-${index}`}
                className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-3 sm:p-6 hover:shadow-md transition-shadow border border-gray-100 dark:border-slate-800"
              >
                <div className="flex gap-3 sm:gap-6">
                  <Link
                    to={`/product/${item.productId}`}
                    className="flex-shrink-0"
                  >
                    <img
                      src={
                        item.image ||
                        "https://placehold.co/150x150?text=No+Image"
                      }
                      alt={item.name}
                      className="w-24 h-24 sm:w-40 sm:h-40 object-contain rounded-lg bg-gray-50 dark:bg-slate-800"
                      onError={(e) => {
                        e.target.src =
                          "https://placehold.co/150x150?text=No+Image";
                      }}
                    />
                  </Link>

                  <div className="flex-grow min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <Link
                        to={`/product/${item.productId}`}
                        className="min-w-0"
                      >
                        <h3 className="text-sm sm:text-lg font-bold text-gray-900 dark:text-white hover:text-blue-600 truncate sm:whitespace-normal">
                          {item.name}
                        </h3>
                      </Link>
                      {!item.isGift && (
                        <button
                          onClick={() => handleRemoveItem(item)}
                          className="text-gray-400 hover:text-red-600 sm:hidden"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="mt-1 sm:mt-2 space-y-0.5 sm:space-y-2 text-[11px] sm:text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        {item.selectedColor && (
                          <p>
                            <span className="font-medium">Màu:</span>{" "}
                            {item.selectedColor}
                          </p>
                        )}
                        {item.selectedSize && (
                          <p>
                            <span className="font-medium">Size:</span>{" "}
                            {item.selectedSize}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <p className="text-sm sm:text-lg font-black text-blue-600 dark:text-blue-400">
                          {formatPrice(item.price)}₫
                        </p>
                        {item.isGift && item.originalPrice && (
                          <p className="text-[10px] sm:text-sm text-red-500 line-through">
                            {formatPrice(item.originalPrice)}₫
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 sm:mt-4">
                      {item.isGift ? (
                        <div className="px-3 py-1 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-[10px] sm:text-sm font-medium text-gray-500 italic">
                          Số lượng: {item.qty} (Quà)
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden bg-gray-50 dark:bg-slate-800">
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item, item.qty - 1)
                              }
                              className="px-2 sm:px-3 py-1 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                            >
                              <svg
                                className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M20 12H4"
                                />
                              </svg>
                            </button>
                            <span className="px-3 sm:px-4 py-1 font-bold text-xs sm:text-base dark:text-white min-w-[32px] sm:min-w-[48px] text-center">
                              {item.qty}
                            </span>
                            <button
                              onClick={() =>
                                handleUpdateQuantity(item, item.qty + 1)
                              }
                              className="px-2 sm:px-3 py-1 hover:bg-white dark:hover:bg-slate-700 transition-colors"
                            >
                              <svg
                                className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                            </button>
                          </div>

                          <button
                            onClick={() => handleRemoveItem(item)}
                            className="hidden sm:block text-red-600 hover:text-red-700 font-bold text-sm"
                          >
                            Xóa
                          </button>
                        </>
                      )}

                      <div className="text-right sm:hidden">
                        <p className="text-[10px] text-gray-400">Tạm tính</p>
                        <p className="text-xs font-bold text-gray-900 dark:text-white">
                          {formatPrice((item.price || 0) * (item.qty || 0))}₫
                        </p>
                      </div>
                    </div>

                    <div className="hidden sm:block mt-4 pt-4 border-t border-gray-200 dark:border-slate-800">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Tạm tính:{" "}
                        <span className="font-bold text-gray-900 dark:text-white">
                          {formatPrice((item.price || 0) * (item.qty || 0))}₫
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 sticky top-24 border border-gray-100 dark:border-slate-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                Tóm tắt đơn hàng
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Tạm tính ({getTotalItems()} sản phẩm)</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {formatPrice(calculateTotal())}₫
                  </span>
                </div>

                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Phí vận chuyển</span>
                  <span className="text-green-600 font-semibold">Miễn phí</span>
                </div>

                <div className="border-t border-gray-100 dark:border-slate-800 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                      Tổng cộng
                    </span>
                    <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {formatPrice(calculateTotal())}₫
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/checkout")}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 dark:shadow-none uppercase tracking-wider"
              >
                Tiến hành đặt hàng
              </button>

              <Link
                to="/products"
                className="block w-full text-center mt-4 px-6 py-3 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                Tiếp tục mua hàng
              </Link>

              <div className="mt-6 pt-6 border-t border-gray-100 dark:border-slate-800 space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="material-symbols-outlined text-green-500">
                    local_shipping
                  </span>
                  <span>Miễn phí giao hàng toàn quốc</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="material-symbols-outlined text-blue-500">
                    verified_user
                  </span>
                  <span>Đổi size trong 7 ngày</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Bottom Bar for Mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-t border-gray-100 dark:border-slate-800 p-3 sm:p-4 animate-slide-up shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium uppercase tracking-widest">
              Toàn bộ giỏ hàng
            </p>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-black text-blue-600 dark:text-blue-400">
                {formatPrice(calculateTotal())}₫
              </span>
              <p className="text-[10px] text-gray-400 italic">
                ({getTotalItems()} món)
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/checkout")}
            className="flex-grow max-w-[200px] bg-gradient-to-r from-blue-600 to-indigo-700 text-white h-12 rounded-xl font-black uppercase text-xs tracking-widest shadow-lg shadow-blue-200 dark:shadow-none flex items-center justify-center gap-2 active:scale-95 transition-transform"
          >
            Thanh toán
            <span className="material-symbols-outlined text-[18px]">
              arrow_forward
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
